"use client"

/**
 * The ONE filter card family (FRIDGE-1088).
 *
 * The owner, on the filter number index: "WHERE ARE LOGOS, FFN ETC AND PHOTOS
 * FIX SITEWIDE". Before this file, six surfaces drew their own filter card:
 * the filter number index as bare text, the filters home, the brand filter
 * pages and the filter number pages each with a private copy, and the finder
 * and the finder tool page each with a third shape that printed the FFN as
 * loose text and simply dropped the photo slot when a row had no photo.
 *
 * Every filter listing now renders from here:
 *
 *   FilterOptionCard  one cartridge: photo, brand logo, title, model number,
 *                     FFN chip, price line. Optional genuine/compatible pill
 *                     for the finder answers.
 *   FilterNumberCard  one filter NUMBER (a per-model hub) — in
 *                     ./filter-number-card.tsx, server-only, so the client
 *                     finder never bundles the hub loaders.
 *   FilterBrandMark   the brand line: the brand's logo when it has one, else
 *                     its name in the card's brand style.
 *   FilterBrandTile   the designed no-photo tile: the brand's logo (or its
 *                     name) with the part number, on a tinted tile. NEVER the
 *                     Fridge.com wordmark — the card's acceptance, and the
 *                     FRIDGE-635 ruling "if no photo use logo of brand".
 *
 * One client boundary shares the existing finder implementation with server
 * pages. Next still server-renders every card; Flight carries only the card
 * data instead of serializing the full rendered tree a second time. The
 * photo renders through SafeImage with the brand logo as its first error
 * fallback, so a dead photo URL degrades to the logo, not a broken icon.
 * Sizing lives in app/globals.css (.filter-*), not in arbitrary utilities.
 *
 * WEIGHT. The filter store renders 30 option cards. Serializing their full
 * trees exceeded the HTML gate even though the finder already shipped this
 * implementation. A production Flight fixture measured 40,079 bytes for the
 * server trees versus 9,352 bytes for the same data at this boundary. Card
 * content, eligibility and the HTML budget stay unchanged.
 */
import Link from 'next/link'
import { PackPrice } from '@/components/filters/pack-price'
import { FilterCardImage } from '@/components/filters/filter-card-image'
import { FfnChip, FilterBrandMark, FilterBrandTile } from '@/components/filters/filter-brand-tile'

export { FfnChip, FilterBrandMark, FilterBrandTile }

export interface FilterOptionCardData {
  href: string
  title: string
  brand: string | null
  /** products.model — the listing's model, which on aftermarket rows is often the OEM code it replaces. */
  model: string | null
  ffn: string | null
  cents: number | null
  image: string | null
  /** Finder answers only: the manufacturer's own cartridge or a replacement. */
  genuine?: boolean
}

/** One cartridge. */
export function FilterOptionCard({
  f,
  logo,
  hideUnpriced = false,
  showKind = false,
  evidenceNote,
}: {
  f: FilterOptionCardData
  logo: string | null
  /** The filters home never printed an unpriced line on its rails; the other surfaces do. */
  hideUnpriced?: boolean
  /** Finder answers carry the genuine / compatible pill. */
  showKind?: boolean
  evidenceNote?: string
}) {
  return (
    <Link href={f.href} className="group cat-card h-full filter-option-card">
      {showKind && (
        <span
          className={`w-fit rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] ${
            f.genuine ? 'bg-[#ff9951] text-[var(--color-brand-action-text)]' : 'bg-[#0469f0] text-white'
          }`}
        >
          {f.genuine ? 'Manufacturer filter' : 'Replacement option'}
        </span>
      )}
      <div className="cat-card__media">
        {f.image ? (
          <FilterCardImage
            src={f.image}
            alt={`${f.title} | Fridge.com`}
            width={220}
            height={220}
            className="object-contain max-h-full"
            tile={[f.brand, logo, f.model]}
          />
        ) : (
          <FilterBrandTile brand={f.brand} logo={logo} code={f.model} />
        )}
      </div>
      <div className="flex-1">
        <FilterBrandMark brand={f.brand} logo={logo} />
        <p className="cat-card__title">{f.title}</p>
        {/* "Model", not "Part number": an aftermarket listing's model field
            often holds the OEM code it replaces, so the label says only what
            the column is (the filters home's wording before FRIDGE-1088). */}
        {f.model && <p className="cat-card__model">Model {f.model}</p>}
        <FfnChip ffn={f.ffn} />
      </div>
      {/* FRIDGE-726: a multi-pack shows the price of ONE filter first. */}
      {hideUnpriced && f.cents === null ? null : <PackPrice title={f.title} cents={f.cents} />}
      {evidenceNote && <p className="text-xs leading-relaxed text-gray-600">{evidenceNote}</p>}
      <span className="cat-card__cta">Compare prices →</span>
    </Link>
  )
}
